import type { TransferTransports } from '@bandido/shared/types';
import type { CancellationPenaltyFields } from '@bandido/shared/types';
import { AxiosError } from 'axios';
import i18next from 'i18next';
import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import {
  PLACE_ID_PATTERN,
  PLACES_AUTOCOMPLETE_LIMITS,
  PLACES_GEOCODE_LIMITS,
  PLACES_LANGS,
  VOUCHER_TEXT_BASE_LANGUAGE,
  VOUCHER_TEXT_LANGUAGES,
  sanitizeRuteroMissingPassengerFields,
} from '@bandido/shared/types';
import type {
  RuteroLuggageSelection,
  VoucherDocumentState,
  VoucherSendChannel,
  VoucherSendResult,
  VoucherTextLanguage,
  GeocodeAutocompleteResponse,
  GeocodePoint,
  PlaceCoordsLookup,
  PlaceKind,
  PlacesAutocompleteResponse,
  PlacesLang,
  RuteroBookingLeg,
  RuteroConfirmedOptionalItem,
  RuteroBookingListMeta,
  RuteroBookingRecoveryMode,
  RuteroBookingSelectedOptions,
  RuteroBookingSupplier,
  RuteroBookingTax,
  RuteroBookingVoucher,
  RuteroConfirmBlock,
  RuteroConnectorCapabilitiesV2,
  RuteroConnectorInfo,
  RuteroCircuitAgeBand,
  RuteroFarePriceComparison,
  RuteroOfferOptionsPricingState,
  RuteroOfferOptionsSelection,
  RuteroOfferOptionsSelectionPreview,
  RuteroOfferQuoteRejectionReason,
  RuteroOccupancy,
  RuteroPickupOption,
  RuteroRefundPolicy,
  RuteroPassengerFieldRef,
  RuteroPassengerType,
  RuteroPaymentType,
  RuteroPricePair,
  RuteroPublicSelectedOptions,
  RuteroQuestionDataType,
  RuteroSelectedOptions,
  RuteroTourDetail,
  RuteroTourSession,
} from '@bandido/shared/types';

import { CONFIG } from '@frontend/config';
import {
  SEARCH_CACHE_MS,
  SEARCH_FRESH_MS,
} from '@frontend/features/common/constants/search';
import { client } from '@frontend/features/common/services';
import { voucherQueryKey } from './voucherAction';
import { QueryKeys } from '@frontend/features/common/types';
import { connectorCapabilitiesQueryOptions } from '@frontend/features/rutero/connectorCapabilitiesQueryKey';
import {
  ownershipCatalogQueryOptions,
  withOwnershipCatalogAuthority,
} from '@frontend/features/rutero/ownershipCatalogAuthority';
import {
  circuitProductPath,
  circuitProductQueryKey,
} from './circuitProductQuery';
import { offerOptionsPreviewRequestPayload } from './offerOptionsSelection';
import { refreshBookingAfterOperationError } from './operationErrorRecovery';
import type { RouteMapPoint } from './routeMap';
import { quoteSearchAuthorityKey } from './selectedQuote';
import {
  BOOKING_TICKET_POLL_DELAYS_MS,
  bookingTicketPollDelay,
  bookingTicketRequestIsActive,
  bookingTicketScopeIsActive,
  bookingTicketsQueryKey,
} from './bookingTicketsQuery';
import { verticalSearchAuthorityOptions } from './searchAuthority';
import { GEOCODE_QUERY_MIN_LENGTH } from './places/geocode';
import { PLACES_PICKER_LIMIT } from './places';

/**
 * El cuerpo de error tal como lo escribe el backend, DESANIDADO.
 *
 * `SentryErrorFilter` es un filtro global (`client-api/src/main.ts`) que envuelve
 * lo que se lanzó: `{statusCode, timestamp, path, message: <lo lanzado>}`. Así
 * que un `throw new ConflictException({code, message})` llega con el `code` un
 * nivel MÁS ADENTRO de donde se lo busca ingenuamente.
 *
 * Esto vive en UN solo lugar a propósito: cuando cada helper desanidaba por su
 * cuenta, se arregló uno y los otros tres quedaron leyendo el nivel de arriba —
 * devolvían `undefined` en silencio y dejaban muerta la recuperación de holds y
 * el aviso de oferta vencida.
 */
function errorEnvelope(error: unknown): Record<string, unknown> | undefined {
  const data = (error as AxiosError<Record<string, unknown>>)?.response?.data;
  if (!data || typeof data !== 'object') return undefined;
  const { message } = data;
  // `message` objeto = lo que el filtro envolvió. String = excepción lanzada con
  // texto suelto, y ahí el envelope es el cuerpo mismo.
  return message && typeof message === 'object'
    ? (message as Record<string, unknown>)
    : data;
}

/**
 * Mensaje legible del backend (ej. "El proveedor X no expone tickets vía API").
 */
export function apiErrorMessage(error: unknown): string | undefined {
  const message = errorEnvelope(error)?.message;
  if (Array.isArray(message) && message.length > 0 && message.every((item) => typeof item === 'string')) {
    return message.join('; ');
  }
  return typeof message === 'string' ? message : undefined;
}

/**
 * El HTTP del backend, para distinguir un rechazo DOCUMENTADO (400: el conector
 * no tiene esa capacidad) de una falla real (502 del proveedor, timeout sin
 * respuesta). Ausente = no hubo respuesta.
 */
export function apiErrorStatus(error: unknown): number | undefined {
  return (error as AxiosError)?.response?.status;
}

export function apiErrorCode(error: unknown): string | undefined {
  const code = errorEnvelope(error)?.code;
  return typeof code === 'string' ? code : undefined;
}

export type TourQuoteRequirement = 'ages' | 'ticket_quantities';

/** Requisitos no personales allowlisted que Rutero aprendió al intentar cotizar. */
export function apiTourQuoteMissingRequirements(
  error: unknown,
): TourQuoteRequirement[] {
  if (apiErrorCode(error) !== 'QUOTE_INPUT_REQUIRED') return [];
  const value = errorEnvelope(error)?.missingRequirements;
  if (!Array.isArray(value)) return [];
  return [
    ...new Set(
      value.filter(
        (entry): entry is TourQuoteRequirement =>
          entry === 'ages' || entry === 'ticket_quantities',
      ),
    ),
  ];
}

/**
 * Vencimiento del hold temporal que Rail ya creo en el proveedor antes de
 * rechazar una seleccion de plaza que Bandido todavia no puede completar.
 *
 * No es la vigencia de la oferta ni un TTL local. Solo se expone cuando el
 * envelope conserva exactamente el shape contractual; una fecha ausente o
 * ilegible queda ausente en vez de producir una hora inventada en la UI.
 */
export function apiRailProviderHoldExpiresAt(error: unknown): string | undefined {
  const providerHold = errorEnvelope(error)?.providerHold;
  if (!providerHold || typeof providerHold !== 'object') return undefined;
  const hold = providerHold as Record<string, unknown>;
  if (
    hold.status !== 'expires_automatically' ||
    typeof hold.expiresAt !== 'string' ||
    hold.expiresAt.trim() === '' ||
    !Number.isFinite(Date.parse(hold.expiresAt))
  ) {
    return undefined;
  }
  return hold.expiresAt;
}

/**
 * El id de la fila LOCAL del intento que el rechazo del hold nombra.
 *
 * Los rechazos que NO son clean-terminal (adopción tomada por otra réplica,
 * recuperación local pendiente, intento en vuelo) viajan con `attemptId` —y a
 * veces con `bookingId`— porque describen una fila write-ahead que existe y
 * puede tener una reserva viva del otro lado. Con ese id la pantalla lee la
 * fila autoritativa en vez de quedarse con un toast que se va solo.
 */
export function apiHoldAttemptId(error: unknown): string | undefined {
  const data = errorEnvelope(error);
  if (typeof data?.attemptId === 'string') return data.attemptId;
  return typeof data?.bookingId === 'string' ? data.bookingId : undefined;
}

export interface HoldRecoveryConflict {
  code: string;
  bookingId: string;
  recoveryMode: RuteroBookingRecoveryMode;
}

export function apiHoldRecoveryConflict(
  error: unknown,
): HoldRecoveryConflict | undefined {
  const data = errorEnvelope(error);
  if (
    data?.code !== 'RUTERO_EXISTING_UNRESOLVED_HOLD' &&
    data?.code !== 'RUTERO_MANUAL_RECONCILIATION_REQUIRED'
  ) {
    return undefined;
  }
  const bookingId =
    typeof data.attemptId === 'string'
      ? data.attemptId
      : typeof data.bookingId === 'string'
        ? data.bookingId
        : undefined;
  if (!bookingId) return undefined;
  if (
    data.recoveryMode !== 'none' &&
    data.recoveryMode !== 'same_attempt_retry' &&
    data.recoveryMode !== 'sync' &&
    data.recoveryMode !== 'manual'
  ) {
    return undefined;
  }
  return {
    code: data.code,
    bookingId,
    recoveryMode: data.recoveryMode,
  };
}

/**
 * Los campos de pasajero que el proveedor exigía y no viajaron
 * (`['contact.phone']`). Salen del envelope, ya validados contra el vocabulario
 * por el backend: se usan para marcar el campo exacto de la fila exacta, sin
 * leer el mensaje. Vacío = no hay nada que marcar y queda el texto del operador.
 */
export function apiMissingPassengerFields(
  error: unknown,
): RuteroPassengerFieldRef[] {
  return sanitizeRuteroMissingPassengerFields(
    errorEnvelope(error)?.missingFields,
  );
}

/**
 * Los ids que la oferta no ofrece (`unknownIds` de un
 * `RUTERO_HOLD_UNKNOWN_OFFER_OPTIONS`), ya acotados por el backend. Con ellos
 * la pantalla decide a qué sección pertenece el rechazo: un código de
 * adicional que viajó en `optionalItemCodes` es de la sección de adicionales.
 * Vacío = no hay nada que atribuir y queda el camino genérico.
 */
export function apiUnknownOfferOptionIds(error: unknown): string[] {
  const raw = errorEnvelope(error)?.unknownIds;
  if (!Array.isArray(raw)) return [];
  return raw.filter((id): id is string => typeof id === 'string' && id.length > 0);
}

export function hasApiErrorResponse(error: unknown): boolean {
  return Boolean((error as AxiosError)?.response);
}

/**
 * La oferta expiró (TTL 15 min) o fue alterada: NO se reintenta con el mismo
 * token, hay que volver a buscar. El backend lo marca con este código.
 */
export function isProvenanceExpired(error: unknown): boolean {
  return apiErrorCode(error) === 'RUTERO_PROVENANCE_EXPIRED';
}

/**
 * Se pidió una búsqueda DIRIGIDA a un proveedor que esta agencia no tiene
 * configurado: el hub rechaza con `CONNECTOR_NOT_CONFIGURED` y el backend lo
 * marca con este código. No es una falla del proveedor ni un resultado vacío
 * —no se llamó a nadie—, así que la pantalla no puede decir "no hay
 * disponibilidad" ni "reintentá": es el estado del CONECTOR (21.18.4).
 */
export function isConnectorNotConfigured(error: unknown): boolean {
  return apiErrorCode(error) === 'RUTERO_CONNECTOR_NOT_CONFIGURED';
}

/**
 * El hub rechazó la búsqueda de traslados porque la fecha de recogida ya pasó
 * (`PICKUP_IN_PAST`: anterior a HOY en UTC−12). El borde propio valida con la
 * MISMA cota (`ruteroMinTransferPickupDate`), así que esto solo llega en una
 * carrera de relojes alrededor del corte — y no es una falla: se muestra como
 * error DEL CAMPO de fecha, nunca como el error genérico de búsqueda.
 */
export function isPickupInPast(error: unknown): boolean {
  return apiErrorCode(error) === 'PICKUP_IN_PAST';
}

/**
 * Vuelta anterior a la ida (`RETURN_BEFORE_DEPARTURE` del hub): mismo
 * tratamiento que `isPickupInPast`, sobre el campo de fecha de vuelta.
 */
export function isReturnBeforeDeparture(error: unknown): boolean {
  return apiErrorCode(error) === 'RETURN_BEFORE_DEPARTURE';
}

/**
 * `RUTERO_OFFER_EXPIRED` (el hold llegó DESPUÉS del vencimiento que el
 * proveedor FIRMÓ en la oferta) no tiene detector propio a propósito: entra
 * por `requiresFreshSearch` (abajo) y su cara —la tarjeta terminal con CTA de
 * re-cotizar— sale de `holdErrorRecoverySurface` por código, como el resto de
 * los rechazos limpios del hold.
 */

/**
 * La cotización actual dejó de ser utilizable y NINGÚN reintento sobre el
 * mismo provenance puede funcionar: hay que volver a buscar. Cubre tres
 * motivos distintos con la misma consecuencia para el asesor:
 *
 * - `RUTERO_PROVENANCE_EXPIRED`: el token venció o fue alterado (el hub lo
 *   rechaza).
 * - `RUTERO_OFFER_EXPIRED`: la oferta venció según el `expiresAt` que FIRMÓ
 *   el proveedor; el hub rechazó sin llamarlo. La tarjeta terminal de este
 *   código además ofrece re-cotizar en el momento.
 * - `RUTERO_HOLD_MARKUP_CONTEXT_UNAVAILABLE`: no hay contexto de markup
 *   durable para congelar el precio de venta, y ese valor es write-once por
 *   intento — el backend rechaza ANTES de despachar, así que reintentar con
 *   la misma oferta solo acumula filas `clean_failed` y el mismo error. El
 *   contexto se recaptura recién en una búsqueda nueva.
 * - `RUTERO_HOLD_RAIL_PASSENGER_COMPOSITION_MISMATCH`: la provenance fue
 *   cotizada para otra composición Rail; el hub exige volver a buscar.
 *
 * Liberar el intento (`shouldResetHoldAttemptAfterError`) NO alcanza: eso
 * sólo rota el `attemptId` y deja el botón de reservar habilitado sobre una
 * oferta que ya no puede reservarse.
 */
export function requiresFreshSearch(error: unknown): boolean {
  const code = apiErrorCode(error);
  return (
    code === 'RUTERO_PROVENANCE_EXPIRED' ||
    code === 'RUTERO_OFFER_EXPIRED' ||
    code === 'RUTERO_OFFER_SELECTION_CHANGED' ||
    code === 'RUTERO_HOLD_MARKUP_CONTEXT_UNAVAILABLE' ||
    code === 'RUTERO_HOLD_RAIL_PASSENGER_COMPOSITION_MISMATCH'
  );
}

export interface RuteroMoney {
  amountMinor: number;
  currency: string;
}

export interface RuteroPricing {
  net: RuteroMoney;
  final: RuteroMoney;
  markupRuleId?: string;
  markupType?: 'percentage' | 'fixed';
  markupValue?: number;
  isGross?: boolean;
}

/** Oferta de search ya priceada por el backend (+ campos propios del vertical). */
export interface PricedSearchResult {
  provider: string;
  /** Token opaco que se canjea en el hold (TTL ~15 min). */
  provenance: string;
  price: RuteroMoney;
  pricing: RuteroPricing;
  /** URLs ya sanitizadas por el backend (solo http/https, máx 5). */
  images?: string[];
  /**
   * Lo que el conector EXIGE de los pasajeros, ya normalizado y allowlisteado
   * por el backend. Se declara `unknown` a propósito: el modal lo vuelve a
   * normalizar (`declaredPassengerFields`) porque también le pueden llegar
   * respuestas cacheadas o de un backend anterior al contrato.
   */
  requiredPassengerInfo?: unknown;
  /** Lo que el conector aprovecha si viene. Nunca bloquea el hold. */
  recommendedPassengerInfo?: unknown;
  // tours
  title?: string;
  description?: string;
  category?: string;
  startDate?: string;
  durationDays?: number;
  /**
   * Franja etaria a la que aplica ESTA oferta. Respaldo de `fareBand`: el
   * proveedor no siempre publica el rótulo.
   */
  ageFrom?: number;
  ageTo?: number;
  /** Rótulo de la banda tarifaria del proveedor ("Adulto", "Niño"). */
  fareBand?: string;
  /** Variante del producto ("Almuerzo"): distingue ofertas por lo demás iguales. */
  variant?: string;
  /** Hora de salida local `HH:MM`; solo si ese día tiene UNA sola sesión. */
  startTime?: string;
  languages?: string[];
  /**
   * El `price` es POR PERSONA. Solo viene si el proveedor lo afirma: un `false`
   * por ausencia mentiría igual que no mandar nada.
   */
  pricePerPassenger?: boolean;
  // circuits
  /** Id del producto: es lo que abre la ficha del circuito. */
  productRef?: string;
  /** Último día INCLUIDO (13 días saliendo el 09/08 terminan el 21/08). */
  endDate?: string;
  /** El proveedor RECHAZA la reserva sin número de pasaporte. */
  passportRequired?: boolean;
  /**
   * El precio de búsqueda NO es el reservable: el total autoritativo recién se
   * conoce al holdear (`quotedPrice`). El contrato lo declara en circuits (el
   * motor del operador aplica tarifas que no publica en ningún endpoint
   * consultable), en tours (precio dinámico según fecha y horario) y en buses
   * (los impuestos de venta se aplican recién al armar la transacción). Por
   * eso el modal lo lee sin rama por vertical.
   */
  priceIsIndicative?: boolean;
  /** Sobre qué es el precio ("per-person-double"). */
  priceBasis?: string;
  /** Franjas etarias publicadas por esta salida de Circuits. */
  ageBands?: RuteroCircuitAgeBand[];
  /** Lo que suma viajar solo, de catálogo (también indicativo). */
  singleSupplement?: RuteroMoney;
  // transfers
  name?: string;
  /**
   * Vencimiento REAL de la oferta firmado por el proveedor (instante UTC con
   * milisegundos, pattern cerrado del contrato). El reloj NO lo lee de acá:
   * la captura de la cotización lo valida y parsea UNA vez
   * (`captureSelectedQuote` → `SelectedTravelQuote.offerExpiresAt`).
   */
  expiresAt?: string;
  /** Frases sueltas del proveedor (equipaje, cancelación), ya acotadas. */
  conditions?: string[];
  /** Horas locales del punto, sin offset. */
  pickUpTime?: string;
  deliveryTime?: string;
  /**
   * Los adicionales OPCIONALES del proveedor, fuera del `price` de la oferta.
   * Se declara `unknown` a propósito, como `requiredPassengerInfo`: el modal lo
   * normaliza (`offerOptionalItems`) porque también le pueden llegar respuestas
   * cacheadas o de un backend anterior al saneo. Son INFORMACIÓN: el contrato
   * del hub todavía no tiene canal para reservarlos (bandido#2333).
   */
  optionalItems?: unknown;
  vehicleCategory?: string;
  vehicleModels?: string;
  passengerCapacity?: number;
  luggageCapacity?: number;
  luggageQuote?: import('@bandido/shared/types').RuteroTransferLuggageQuote;
  includedWaitingMinutes?: number;
  instantConfirmation?: boolean;
  /** Hora local del punto, sin offset (formato del proveedor). */
  freeCancellationUntil?: string;
  /**
   * Precio UNITARIO cuando el proveedor cotiza por pasajero: `price` y
   * `pricing` ya vienen totalizados por el backend, esto es para mostrarlo.
   */
  unitPrice?: RuteroMoney;
  // buses (horarios LOCALES de cada parada, sin offset)
  /** Comercializadora (con quién se vende) y transportista (quién maneja). */
  carrier?: string;
  departure?: string;
  arrival?: string;
  /** Tramos del servicio; UN tramo = directo (puede parar igual: ver stopCount). */
  segments?: { platform?: string; [key: string]: unknown }[];
  /** La calidad de ESTA oferta (Semi-cama, Cama...): el precio es de ella. */
  seatClass?: {
    code?: string;
    name?: string;
    price?: RuteroMoney;
    freeSeats?: number;
  };
  /** Calidades hermanas del mismo servicio: cada una llega también como oferta propia. */
  otherSeatClasses?: { code?: string; name?: string; price?: RuteroMoney }[];
  freeSeats?: number;
  accessibleSeats?: number;
  stopCount?: number;
  onboardAttendant?: boolean;
  /** Ida y vuelta en dos pasos: qué tramo es esta oferta. */
  leg?: 'outbound' | 'return';
  /** Solo en el paso 2: el total ida+vuelta según el proveedor. */
  roundTripTotal?: RuteroMoney;
  // ferry (los horarios son LOCALES del puerto, sin offset)
  fromPort?: string;
  toPort?: string;
  returnDeparture?: string;
  returnArrival?: string;
  durationMinutes?: number;
  // cars
  carModel?: string;
  sippCode?: string;
  company?: string;
  doors?: string;
  passengers?: string;
  bags?: string;
  transmission?: string;
  airConditioning?: string;
  kmIncluded?: string;
  pickUpLocation?: string;
  dropOffLocation?: string;
  /** Desglose informativo del proveedor (base comisionable / tasas). */
  approxBase?: RuteroMoney;
  approxTax?: RuteroMoney;
  // esims
  coverage?: string;
  countryCode?: string;
  operator?: string;
  planType?: string;
  dataAmount?: string;
  voiceMinutes?: number;
  textMessages?: number;
  unlimited?: boolean;
  validityDays?: number;
  activationPolicy?: string;
  requiresRoaming?: boolean;
  apn?: string;
  /** PVP sugerido por el proveedor (informativo, D6). */
  retailPrice?: RuteroMoney;
  [key: string]: unknown;
}

/**
 * Oferta que puede entrar al modal de reserva. Tours v2 permite abrir el
 * discovery sin precio; el importe firme aparece recién en el quote.
 */
export interface BookingModalOffer extends Partial<PricedSearchResult> {
  provider: string;
  provenance: string;
  price?: RuteroMoney;
  priceFrom?: RuteroMoney;
  pricing?: RuteroPricing;
}

export function isPricedSearchResult(
  offer: BookingModalOffer,
): offer is PricedSearchResult {
  return Boolean(offer.price && offer.pricing);
}

export interface ProvidersOutcome {
  responded: string[];
  failed: { provider: string; reason: string; code?: string }[];
  timedOut: string[];
  skipped?: {
    provider: string;
    connector: string;
    reason: 'place_not_mapped' | 'system_not_known' | 'resolver_unavailable';
    places: string[];
  }[];
  truncated?: { provider: string; returned: number; total?: number }[];
  degraded?: {
    provider: string;
    code: 'CURRENCY_NOT_AVAILABLE';
    reason: string;
  }[];
}

export interface SearchEnvelope {
  results: PricedSearchResult[];
  providers: ProvidersOutcome;
}

export interface ToursSearchPayload {
  from?: string;
  to?: string;
  /** Lugar canónico de Ubikeishon; Rutero lo resuelve por conector. */
  destination: string;
  partySize?: number;
  lang?: string;
  providers?: string[];
}

export interface TransferPointPayload {
  kind: 'place' | 'ref' | 'detail';
  /** Solo `kind === 'place'`: lugar canónico de ubikeishon (`plc_*`), tal cual — nunca se traduce del lado de Bandido (#2178). */
  place?: string;
  ref?: string;
  type?: 'A' | 'H' | 'S' | 'P';
  name?: string;
  address?: string;
  town?: string;
  country?: string;
  zip?: string;
  iata?: string;
  lat?: number;
  lng?: number;
}

export interface RailSearchPayload {
  fromStation: string;
  toStation: string;
  date: string;
  passengers?: number | RailPassengerPayload[];
}

export interface RailPassengerPayload {
  type: 'adult' | 'youth' | 'senior';
  age?: number;
}

export interface FerrySearchPayload {
  fromPort: string;
  toPort: string;
  date: string;
  returnDate?: string;
  passengers?: number;
  /** Edades REALES (el primero adulto): los cortes son de la ruta. */
  passengerAges?: number[];
  vehicleRef?: string;
  currency?: string;
}

/**
 * Lo que una opción elegida SUMA a la oferta, ya resuelto por el backend:
 * `net` es el adicional neto del operador, `final` lo que suma al precio de
 * venta con el markup de la agencia proyectado. Acá solo se formatea y se
 * suma; ninguna comparación ni resta de precios vive en el frontend.
 */
export type OptionSupplement = RuteroPricePair;

/** Acomodación ofrecida. Lo que suma elegirla está en `supplement`. */
export interface AccommodationOption {
  id: string;
  name: string;
  kind: 'cabin' | 'seat' | 'deck' | 'other';
  capacity?: number;
  shared?: boolean;
  gender?: string;
  availability?: number;
  /**
   * NO es la señal de que la travesía exige plaza: el proveedor se contradice
   * entre sus endpoints y acá suele llegar en `false`. La autoritativa es
   * `accommodationCompulsory` de la oferta.
   */
  compulsory?: boolean;
  leg?: 'outbound' | 'return';
  /**
   * Precio CRUDO del proveedor: NO se usa para pintar adicionales ni sumar (la
   * plaza incluida trae precio propio aunque no se cobre). Para eso está
   * `supplement`.
   */
  price?: RuteroMoney;
  /** La plaza ya está cubierta por el precio: no es un recargo. */
  includedInPrice?: boolean;
  /**
   * La plaza que QUEDA reservada si no se elige ninguna (la incluida en la
   * tarifa). Con selección, la reemplaza. Lo dice el hub, no se deduce.
   */
  default?: boolean;
  /** Cuántos pasajeros cubre UNA entrada de esta plaza (dato del hub, para pintar). */
  coversPassengers?: number;
  /**
   * Adicional INDICATIVO publicado por el operador, resuelto por el backend:
   * 0 si está incluida, `null` si no se puede comparar. El precio que manda
   * es el del quote con la selección (`useOfferOptionsPreview`).
   */
  supplement?: OptionSupplement | null;
  /** Geometría del asiento (buses): con fila/columna/piso se dibuja la unidad. */
  row?: number;
  column?: number;
  deck?: number;
}

/** Tipo de tarifa; su `price` es el TOTAL de la oferta con esa tarifa. */
export interface TicketTypeOption {
  id: string;
  name: string;
  description?: string;
  conditions?: string[];
  price?: RuteroMoney;
  /**
   * Cuánto MÁS que la tarifa base cuesta esta, resuelto por el backend. `null`
   * cuando no es comparable (`farePriceComparison: 'unknown'`).
   */
  supplement?: OptionSupplement | null;
}

/**
 * Cómo se comparan los precios de las tarifas, decidido por el backend:
 * `same` = todas con precio comparable e igual; `different` = todas con precio
 * y alguna difiere; `unknown` = falta algún precio o hay monedas mezcladas (no
 * se afirma nada).
 */
export type FarePriceComparison = RuteroFarePriceComparison;

/**
 * Si el backend pudo cotizar los `supplement`. `unavailable`: no tenía el
 * contexto comercial de la oferta (provenance vencido, cache compartido) — no
 * se muestra ningún adicional; el hold cotiza con autoridad.
 */
export type OfferOptionsPricingState = RuteroOfferOptionsPricingState;

export interface OfferOptions {
  pickups?: RuteroPickupOption[];
  refundPolicies?: RuteroRefundPolicy[];
  accommodations: AccommodationOption[];
  ticketTypes: TicketTypeOption[];
  message?: string;
  /**
   * La travesía EXIGE plaza para cada pasajero (el operador rechaza el booking
   * sin ella). Viaja también acá (no solo en la oferta del search) porque el
   * modal puede quedarse sin la oferta. TRI-estado: ausente ≠ `false` — ausente
   * cae al valor de la oferta.
   */
  accommodationCompulsory?: boolean;
  /** Ausente en respuestas de un backend anterior: se trata como `unknown`. */
  farePriceComparison?: FarePriceComparison;
  /** Ausente en respuestas de un backend anterior: se trata como `unavailable`. */
  pricingState?: OfferOptionsPricingState;
  /**
   * Pasajeros que la selección tiene que cubrir según el operador (infantes
   * ya descontados). Solo cuando la travesía publica acomodaciones. Se pinta;
   * la validación la hace el operador vía el quote.
   */
  passengersToCover?: number;
  sessions?: RuteroTourSession[];
}

/**
 * Contrato del preview: es EL MISMO tipo de `@bandido/shared` que publica el
 * backend, re-exportado con nombre local. Una sola definición: agregar un
 * estado o un `reason` en shared se ve acá en tiempo de compilación.
 */
export type SelectedOptions = RuteroSelectedOptions;
export type OfferQuoteRejectionReason = RuteroOfferQuoteRejectionReason;
export type OfferOptionsSelection = RuteroOfferOptionsSelection;
export type OfferOptionsSelectionPreview = RuteroOfferOptionsSelectionPreview;

/** Discovery progresivo: provenance + composición conocida + selección actual. */
export interface OfferOptionsRequest {
  provenanceToken: string;
  partySize?: number;
  distribution?: { ages: number[] };
  selection?: OfferOptionsSelection;
}

export interface CircuitsSearchPayload {
  from: string;
  to?: string;
  productRef?: string;
  /** El `productRef` es provider-scoped: la búsqueda va dirigida a su dueño. */
  providers?: string[];
  destination?: string;
  pax?: number;
  /** Cuando viene, reemplaza semánticamente al alias legacy `pax`. */
  occupancy?: RuteroOccupancy;
  lang?: string;
}

export interface BookingExtra {
  extraId: string;
  name: string;
  description?: string;
  price?: RuteroMoney;
  validFrom?: string;
  validTo?: string;
  /**
   * Qué hace el POST con `date` para este extra: 'none' la ignora, 'optional'
   * la acepta dentro de la vigencia, 'required' rechaza sin ella. Ausente
   * equivale a 'none' (contrato). Se lee SIEMPRE vía `extraDateSelection`.
   */
  dateSelection?: string;
  /** Tramo dueño: el mismo id opaco que `booking.legs[]`. Ausente = el proveedor no distingue tramos. */
  legId?: string;
  /** Sentido del tramo dueño ('outbound' | 'return'); presente siempre que haya `legId`. */
  direction?: string;
  /**
   * Cuántas unidades como máximo acepta el operador para este extra (0 es
   * real: no lo acepta). Se lee SIEMPRE vía `extraMaxAllowed`.
   */
  maxAllowed?: number;
}

export interface PurchasedExtra {
  purchaseId: string;
  extraId: string;
  name?: string;
  date?: string;
  passengerRef?: string;
  price?: RuteroMoney;
  /** Tramo dueño de esta compra: el mismo id que `booking.legs[]` y que `available[].legId`. */
  legId?: string;
  /** Sentido del tramo dueño de esta compra. */
  direction?: string;
}

export interface ExtrasResult {
  available: BookingExtra[];
  purchased: PurchasedExtra[];
}

export type ConnectorCapabilities = RuteroConnectorCapabilitiesV2;

/**
 * Vista operacional del conector. Reusa el contrato compartido y hace
 * opcionales solo las autoridades v2 porque la respuesta HTTP es no confiable:
 * si faltan, los gates fallan cerrados.
 */
export type ConnectorInfo = Pick<RuteroConnectorInfo, 'provider' | 'vertical' | 'connector' | 'configured'> &
  Partial<Pick<RuteroConnectorInfo, 'passengerTypes' | 'occupancyLimits'>> &
  Partial<Pick<RuteroConnectorInfo, 'supportedCapabilities' | 'availableCapabilities' | 'unavailableReasons'>>;

export interface PassPassengerPayload {
  type: 'adult' | 'youth' | 'senior';
  age: number;
  nationality: string;
  countryOfResidence: string;
}

export interface PassesSearchPayload {
  passengers: PassPassengerPayload[];
  product?: string;
  travelClass?: '1stclass' | '2ndclass';
  currency?: string;
}

export interface EsimsSearchPayload {
  country?: string;
  /** Lugar del maestro (`plc_*`, #2174) — si viene, gana sobre `country`. */
  destination?: string;
  region?: string;
  type?: 'local' | 'global';
}

export interface CatalogEntry {
  id: string;
  name: string;
  type?: string;
  countryCode?: string;
  lat?: number;
  lng?: number;
  /** Extras del proveedor (ej. `searchBy: coordenadas`, oficinas cercanas). */
  meta?: Record<string, unknown>;
  /**
   * Qué proveedor publica esta entrada. Solo la traen los catálogos que mezclan
   * varios (circuitos): en los de un solo proveedor no aporta nada.
   */
  provider?: string;
}

/**
 * Forma legacy del cache de estaciones. No participa de búsquedas ni mapas
 * canónicos; permanece tipada mientras otros consumidores retiran esa ruta.
 */
export interface CarsSearchPayload {
  pickUpCode?: string;
  pickUpLat?: number;
  pickUpLng?: number;
  dropOffCode?: string;
  dropOffLat?: number;
  dropOffLng?: number;
  pickUpDate: string;
  pickUpTime: string;
  dropOffDate: string;
  dropOffTime: string;
  country: string;
  source: string;
  paymentType?: 'ppd' | 'pod';
  providers?: string[];
  ratePlans?:
    | { mode: 'best' }
    | { mode: 'all' }
    | { mode: 'selected'; ids: string[] };
}

export interface TransfersSearchPayload {
  luggage?: RuteroLuggageSelection;
  tripType: 'ONEWAYTRIP' | 'ROUNDTRIP';
  origin: TransferPointPayload;
  destination: TransferPointPayload;
  date: string;
  endDate?: string;
  originTime: string;
  destinationTime?: string;
  occupants: { type: 'ADL' | 'CHL'; age?: number }[];
  lang?: string;
  currency?: string;
}

/**
 * Catálogo COMPLETO de destinos/regiones de eSIMs (una sola llamada: es
 * estático y el backend lo cachea). El filtrado y la traducción de nombres
 * son client-side, así el autocomplete matchea en el idioma del usuario y no
 * solo en el inglés del proveedor.
 */
export const useEsimDestinations = () => {
  const query = useQuery({
    ...ownershipCatalogQueryOptions(CONFIG.AGENCY_ID, 'esims', 'destinations'),
    queryFn: async (): Promise<CatalogEntry[]> => {
      const response = await client.get<{ results: CatalogEntry[] }>(
        'rutero/catalog/esims/destinations',
        { params: { limit: 500 } },
      );
      return response.data.results;
    },
    staleTime: 30 * 60 * 1000,
    retry: false,
  });
  return withOwnershipCatalogAuthority(query);
};

/**
 * Destinos ALCANZABLES desde un puerto: qué conecta el proveedor de verdad.
 * Que dos puertos existan no significa que haya travesía entre ellos, y una
 * búsqueda vacía se lee como "no hay lugar" cuando en realidad la ruta no
 * existe.
 *
 * `from` es prácticamente obligatorio: sin él el hub recorre el grafo entero
 * (~7.900 rutas, ~25 s la primera vez). Con él es una llamada y después cache.
 */
export const useFerryRoutes = (fromCode?: string) => {
  const query = useQuery({
    ...ownershipCatalogQueryOptions(
      CONFIG.AGENCY_ID,
      'ferry',
      'routes',
      fromCode,
    ),
    queryFn: async (): Promise<CatalogEntry[]> => {
      const response = await client.get<{ results: CatalogEntry[] }>(
        'rutero/catalog/ferry/routes',
        { params: { from: fromCode } },
      );
      return response.data.results;
    },
    enabled: Boolean(fromCode),
    staleTime: 60 * 60 * 1000,
    retry: false,
  });
  return withOwnershipCatalogAuthority(query);
};

/**
 * ¿Existe AL MENOS UNA ruta entre estos dos lugares? Punto de chequeo barato
 * (catálogo cacheado, no una búsqueda priceada) para el picker de PLACES:
 * origen y destino se eligen libremente como referencias `plc_*`, así que sin
 * esto la única señal de "no hay ruta" sería la búsqueda priceada vacía. El
 * endpoint recibe `from` y `to` intactos; Bandido no los traduce a códigos de
 * puerto (mismo contrato, ver docs/RUTERO.md §3).
 */
export const useFerryRouteConnectivity = (fromId?: string, toId?: string) => {
  const query = useQuery({
    ...ownershipCatalogQueryOptions(
      CONFIG.AGENCY_ID,
      'ferry',
      'route-connectivity',
      fromId,
      toId,
    ),
    queryFn: async (): Promise<boolean> => {
      const response = await client.get<{ results: CatalogEntry[] }>(
        'rutero/catalog/ferry/routes',
        { params: { from: fromId, to: toId, limit: 1 } },
      );
      return response.data.results.length > 0;
    },
    enabled: Boolean(fromId && toId),
    staleTime: 60 * 60 * 1000,
    retry: false,
  });
  return withOwnershipCatalogAuthority(query);
};

/**
 * Modelos de vehículo: ~9k entradas, así que este SÍ es remoto — `q` viaja al
 * hub con debounce.
 */
export const useFerryVehicleModels = (q: string) => {
  const query = useQuery({
    ...ownershipCatalogQueryOptions(
      CONFIG.AGENCY_ID,
      'ferry',
      'vehicle-models',
      q,
    ),
    queryFn: async (): Promise<CatalogEntry[]> => {
      const response = await client.get<{ results: CatalogEntry[] }>(
        'rutero/catalog/ferry/vehicle-models',
        { params: { q, limit: 25 } },
      );
      return response.data.results;
    },
    enabled: q.trim().length >= 2,
    staleTime: 30 * 60 * 1000,
    retry: false,
  });
  return withOwnershipCatalogAuthority(query);
};

/**
 * Opciones a elegir sobre una oferta (ferry: acomodación y tarifa; buses:
 * asientos y calidad) entre buscar y holdear. Listas vacías es una respuesta
 * válida ("no hay nada que elegir"); un proveedor sin la capacidad responde
 * 400, que se muestra como aviso, no como error. Los precios vuelven ya
 * interpretados y cotizados por el backend con el contexto comercial de la
 * oferta (`supplement`, `farePriceComparison`, `pricingState`): acá viaja
 * SOLO el token.
 */
export const useOfferOptions = (vertical: string) =>
  useMutation({
    mutationFn: async (payload: OfferOptionsRequest): Promise<OfferOptions> => {
      const response = await client.post<OfferOptions>(
        `rutero/${vertical}/offer-options`,
        payload,
      );
      return response.data;
    },
    retry: false,
  });

/**
 * Precio previsualizado con la selección actual, calculado por el backend.
 * Se re-pide con cada cambio de selección; `keepPreviousData` evita que el
 * total parpadee entre clicks. La key lleva la agencia, la oferta y la
 * selección completa: otra selección es otro precio.
 */
export const useOfferOptionsPreview = (
  vertical: string,
  provenanceToken: string | undefined,
  selection: OfferOptionsSelection,
  enabled: boolean,
  passengers?: RuteroPassengerPayload[],
  distribution?: { ages: number[] },
  /** Fuerza un quote nuevo sin alterar el payload contractual. */
  quoteRefreshId?: string,
  /** Cantidad real elegida para el contexto comercial del quote. */
  partySize?: number,
  luggage?: RuteroLuggageSelection,
) =>
  useQuery({
    queryKey: [
      QueryKeys.getRuteroOfferOptionsPreview,
      CONFIG.AGENCY_ID,
      vertical,
      provenanceToken,
      selection.pickupId ?? '',
      selection.ticketQuantities ?? [],
      selection.accommodationIds ?? [],
      selection.ticketTypeId ?? '',
      selection.sessionId ?? '',
      selection.occupancy ?? null,
      selection.supplements ?? [],
      selection.quoteToken ?? '',
      // Otra selección de adicionales es otra cotización (#2383).
      selection.optionalItemCodes ?? [],
      passengers ?? [],
      distribution ?? null,
      quoteRefreshId ?? '',
      partySize ?? null,
      luggage ?? null,
    ],
    queryFn: async (): Promise<OfferOptionsSelectionPreview> => {
      const response = await client.post<OfferOptionsSelectionPreview>(
        `rutero/${vertical}/offer-options/preview`,
        offerOptionsPreviewRequestPayload({
          provenanceToken,
          selection,
          quoteRefreshId,
          distribution,
          passengers,
          partySize,
          luggage,
        }),
      );
      return response.data;
    },
    enabled: enabled && Boolean(provenanceToken),
    keepPreviousData: true,
    retry: false,
    // Un POST de quote no se repite por recuperar foco: cada generación se
    // cotiza una vez y reutiliza su cache compartido/single-flight.
    refetchOnWindowFocus: false,
  });

export const useTourOfferDetail = () =>
  useMutation({
    mutationFn: async (payload: { provenanceToken: string }): Promise<RuteroTourDetail> => {
      const response = await client.post<RuteroTourDetail>(
        'rutero/tours/offer-detail',
        payload,
      );
      return response.data;
    },
    retry: false,
  });

export const useSearchFerry = (payload: FerrySearchPayload | null) =>
  useVerticalSearch('ferry', payload);

/**
 * Micros. El ida y vuelta es de DOS PASOS: con `returnDate` la respuesta trae
 * solo idas; elegida una, se busca con `outboundOfferRef` (su provenance) y
 * llegan las vueltas con el total encadenado. Con el token, el resto sobra.
 */
export interface BusesSearchPayload {
  fromStation?: string;
  toStation?: string;
  date?: string;
  returnDate?: string;
  passengers?: number;
  outboundOfferRef?: string;
}

export const useSearchBuses = (payload: BusesSearchPayload | null) =>
  useVerticalSearch('buses', payload);

/** Tope de la lista del PICKER: es un dropdown y el ranking del hub manda. */
const BUS_STATIONS_PICKER_LIMIT = 20;

/**
 * Tope de la RESOLUCIÓN de una parada comiteada. Holgado a propósito, por el
 * mismo motivo que el cache de estaciones de tren: el hub filtra por nombre
 * CONTIENE y no promete ningún orden, así que con un tope chico la parada que
 * se busca —o la que discrepa con ella— puede quedar afuera de la ventana.
 * Cuando eso pasa igual no se resuelve nada (ver `BusStationsCatalogPage`): el
 * tope holgado es para que la degradación a "sin card" casi nunca ocurra, no
 * para tapar el caso.
 */
const BUS_STATIONS_RESOLUTION_LIMIT = 200;

/**
 * Una página del catálogo de paradas con lo necesario para saber si está
 * COMPLETA. Resolver una referencia sobre una lista incompleta es peor que no
 * resolverla: el candidato que discrepa puede ser justo el que falta, y
 * entonces la "coincidencia única" es un artefacto de lo que no vino.
 *
 * Se puede faltar por dos motivos distintos y hay que descartar los dos:
 *
 * - **Un proveedor del fan-out no contestó** (`providers.failed` /
 *   `providers.timedOut` del envelope, los dos declarados y requeridos por el
 *   contrato del hub): sus paradas no están, y una de ellas puede tener el
 *   mismo código con otra coordenada.
 * - **Vinieron menos entradas de las que matchean**: eso lo dice el `total`
 *   del envelope, que el contrato declara REQUERIDO y es la única fuente de
 *   la verdad al respecto. Haber vuelto por debajo del tope pedido NO prueba
 *   nada: `limit` es un máximo, no una promesa de completitud, y un `total`
 *   mayor que lo que vino contradice esa lectura. Sin un `total` legible
 *   —ausente, no entero, negativo— no se afirma nada.
 *
 * (`CatalogEnvelope` no declara acuse de truncamiento POR PROVEEDOR:
 * `truncated` existe en el envelope de BÚSQUEDA, no en este. El faltante que
 * eso produciría lo delata igual el `total`.)
 */
export interface BusStationsCatalogPage {
  results: CatalogEntry[];
  /**
   * `total` del envelope: cuántas entradas matchean la consulta, antes del
   * tope. Requerido por el contrato, opcional acá porque es contenido de un
   * tercero — y sin él no hay prueba de completitud.
   */
  total?: number;
  /**
   * Cuántos proveedores declara el hub que NO contestaron (`failed` +
   * `timedOut`). `undefined` = el acuse no vino o no tiene la forma
   * declarada: lo que no se puede leer no se asume en cero.
   */
  missingProviders?: number;
}

/**
 * Proveedores que no contestaron, o `undefined` si el acuse no se puede leer.
 * Es contenido de un tercero: se valida la forma antes de contar (21.11).
 */
function missingProviderCount(providers: unknown): number | undefined {
  if (!providers || typeof providers !== 'object') return undefined;
  const { failed, timedOut } = providers as Partial<ProvidersOutcome>;
  if (!Array.isArray(failed) || !Array.isArray(timedOut)) return undefined;
  return failed.length + timedOut.length;
}

/**
 * Paradas de micro (1.392): búsqueda remota como las estaciones de tren — `q`
 * filtra en el hub (por NOMBRE, "contiene", case-insensitive), porque la lista
 * completa supera el cap del catálogo.
 *
 * Una sola definición del request (21.22) para sus dos consumidores, que piden
 * TAMAÑOS distintos: el picker, una lista corta para elegir; la resolución del
 * mapa, una holgada donde no se pierda la coincidencia exacta. Por eso el
 * `limit` entra en la query key: sin él las dos lecturas del mismo texto
 * compartirían entrada de cache y la que llegara primero le serviría a la otra
 * una lista del tamaño equivocado.
 */
async function fetchBusStations(
  q: string,
  limit: number,
): Promise<BusStationsCatalogPage> {
  const response = await client.get<{
    results: CatalogEntry[];
    total?: number;
    providers?: ProvidersOutcome;
  }>('rutero/catalog/buses/stations', { params: { q, limit } });
  const { results, total, providers } = response.data;
  const missingProviders = missingProviderCount(providers);
  return {
    results,
    ...(typeof total === 'number' ? { total } : {}),
    ...(missingProviders === undefined ? {} : { missingProviders }),
  };
}

const busStationsQueryOptions = (text: string, limit: number) => ({
  ...ownershipCatalogQueryOptions(
    CONFIG.AGENCY_ID,
    'buses',
    'stations',
    text,
    limit,
  ),
  enabled: text.trim().length >= 2,
  staleTime: 30 * 60 * 1000,
  retry: false,
});

/**
 * Las paradas como AUTORIDAD DE SELECCIÓN (el picker): filas de un refetch en
 * vuelo o fallado no pueden alimentar una selección nueva. Acá alcanza con las
 * filas: el asesor elige de lo que ve, no se resuelve nada contra la lista.
 */
export const useBusStations = (q: string) => {
  const query = useQuery({
    ...busStationsQueryOptions(q, BUS_STATIONS_PICKER_LIMIT),
    queryFn: async (): Promise<CatalogEntry[]> =>
      (await fetchBusStations(q, BUS_STATIONS_PICKER_LIMIT)).results,
  });
  return withOwnershipCatalogAuthority(query);
};

/**
 * Las paradas como DISPLAY, para resolver la referencia comiteada a
 * coordenadas (el mapa del recorrido). Sin el filtro de autoridad, por el
 * mismo motivo que `usePlaceCoords` y el cache de estaciones de tren:
 * esconder la geo durante un refetch apagaría el mapa sin que haya pasado
 * nada, y acá el catálogo no alimenta ninguna selección.
 *
 * Devuelve la PÁGINA, no las filas: resolver una referencia exige saber si se
 * vieron todos los candidatos.
 */
export const useBusStationsResolution = (q: string | undefined) => {
  const text = q ?? '';
  return useQuery({
    ...busStationsQueryOptions(text, BUS_STATIONS_RESOLUTION_LIMIT),
    queryFn: () => fetchBusStations(text, BUS_STATIONS_RESOLUTION_LIMIT),
  });
};

/**
 * Circuitos del catálogo del proveedor. Son ~25k productos MEZCLANDO temporadas
 * viejas y vigentes, así que se busca con `q` y se pide un lote grande para
 * poder filtrar por temporada del lado del cliente: elegir un circuito de una
 * temporada pasada da una búsqueda sin salidas, que parece un error y no lo es.
 */
/** Un día del itinerario de un circuito. */
export interface CircuitDay {
  day: number;
  title?: string;
  description?: string;
  detail?: string;
  videoUrl?: string;
}

/** Excursión opcional de un circuito, contratable aparte del paquete. */
export interface CircuitOptional {
  id: string;
  name: string;
  city?: string;
  description?: string;
  includes?: string;
  price?: RuteroMoney;
  /** Minutos, cuando el hub pudo interpretar el texto del operador. */
  durationMinutes?: number;
  /** El texto crudo del operador ("media jornada"), si no hay minutos. */
  duration?: string;
  videoUrl?: string;
}

/**
 * Ficha de un circuito. Casi todo es opcional y varía por producto: hay
 * circuitos con 13 días de itinerario y ningún folleto, y otros al revés.
 */
export interface CircuitProduct {
  productRef: string;
  name: string;
  description?: string;
  images?: string[];
  durationDays?: number;
  includes?: string[];
  itinerary?: CircuitDay[];
  optionals?: CircuitOptional[];
  guaranteedDeparture?: boolean;
  immediateConfirmation?: boolean;
  passportRequired?: boolean;
  brochureUrls?: string[];
  videoUrl?: string;
  provider: string;
}

/**
 * Ficha del circuito para la página de detalle (catálogo: no lleva provenance).
 * El `provider` es parte de la IDENTIDAD del producto, no un extra: el ref es
 * provider-scoped (ver `circuitProductQueryKey`).
 */
export const useCircuitProduct = (productRef?: string, provider?: string) =>
  useQuery({
    queryKey: circuitProductQueryKey(CONFIG.AGENCY_ID, productRef, provider),
    queryFn: async (): Promise<CircuitProduct> => {
      const response = await client.get<CircuitProduct>(
        // El ref es OPACO (contrato del hub): hoy VPT trae ":", mañana puede
        // ser cualquier cosa — siempre URL-encodeado, nunca crudo en el path.
        circuitProductPath(productRef, provider),
      );
      return response.data;
    },
    enabled: Boolean(productRef),
    staleTime: 30 * 60 * 1000,
    retry: false,
  });

export const useCircuitProducts = (q: string) => {
  const query = useQuery({
    ...ownershipCatalogQueryOptions(
      CONFIG.AGENCY_ID,
      'circuits',
      'products',
      q,
    ),
    queryFn: async (): Promise<CatalogEntry[]> => {
      const response = await client.get<{ results: CatalogEntry[] }>(
        'rutero/catalog/circuits/products',
        { params: { q, limit: 1000 } },
      );
      return response.data.results;
    },
    enabled: q.trim().length >= 3,
    staleTime: 30 * 60 * 1000,
    retry: false,
  });
  return withOwnershipCatalogAuthority(query);
};

/**
 * De cuándo es el catálogo rastreado de cada proveedor. Se muestra: quien arma
 * un paquete tiene que saber con qué fecha trabaja.
 */
export interface CatalogFreshness {
  provider: string;
  total: number;
  fetchedAt: string;
}

/** Un destino del multibuscador, con cuántos circuitos pasan por él. */
export interface CircuitDestination {
  name: string;
  circuits: number;
  providers: string[];
}

/**
 * Vocabulario de destinos del multibuscador. Los `name` vienen normalizados y
 * son EXACTAMENTE los valores que acepta el filtro `cities`: se copian tal
 * cual, cualquier retoque los deja de matchear.
 */
export const useCircuitDestinations = (q: string) =>
  useQuery({
    queryKey: [
      QueryKeys.getRuteroCatalog,
      'circuits',
      'destinations',
      CONFIG.AGENCY_ID,
      q,
    ],
    queryFn: async (): Promise<{
      results: CircuitDestination[];
      catalog?: CatalogFreshness[];
    }> => {
      const response = await client.get<{
        results: CircuitDestination[];
        catalog?: CatalogFreshness[];
      }>('rutero/circuits/destinations', { params: { q, limit: 30 } });
      return response.data;
    },
    enabled: q.trim().length >= 2,
    staleTime: 30 * 60 * 1000,
    retry: false,
  });

/**
 * Lo que trae cada card del catálogo, ya saneado y priceado por el backend.
 * `pricing` es el "desde" con markup, en base comparable entre proveedores; el
 * `minPrice` crudo del proveedor no cruza la frontera a propósito.
 */
export interface CircuitCatalogMeta {
  cities?: string[];
  durationDays?: number;
  images?: string[];
  pricing?: RuteroPricing;
  /** Siempre true en el catálogo: descubre, no cotiza. */
  priceIsIndicative?: boolean;
}

/** Filtros del multibuscador. Sin ninguno no se consulta: sería el catálogo entero. */
export interface CircuitCatalogFilters {
  cities: string[];
  minDays?: number;
  maxDays?: number;
}

/**
 * Multibuscador de circuitos: DESCUBRE, no cotiza. De acá no sale ningún
 * precio vendible ni token de provenance — un dato viejo del catálogo no puede
 * convertirse en reserva por accidente. Elegido el circuito, la search dirigida
 * va al proveedor en vivo.
 */
export const useCircuitCatalog = (filters: CircuitCatalogFilters | null) => {
  const query = useQuery({
    ...ownershipCatalogQueryOptions(
      CONFIG.AGENCY_ID,
      'circuits',
      'catalog',
      filters,
    ),
    queryFn: async (): Promise<{
      results: CatalogEntry[];
      total: number;
      catalog?: CatalogFreshness[];
    }> => {
      const response = await client.get<{
        results: CatalogEntry[];
        total: number;
        catalog?: CatalogFreshness[];
      }>('rutero/catalog/circuits/products', {
        params: {
          ...(filters?.cities.length ? { cities: filters.cities.join(',') } : {}),
          ...(filters?.minDays ? { minDays: filters.minDays } : {}),
          ...(filters?.maxDays ? { maxDays: filters.maxDays } : {}),
          limit: 200,
        },
      });
      return response.data;
    },
    enabled: Boolean(filters),
    staleTime: 30 * 60 * 1000,
    retry: false,
  });
  return withOwnershipCatalogAuthority(query);
};

/**
 * Pases de tren. SIN filtro de producto a propósito: el proveedor devuelve
 * todos los bundles disponibles para esos pasajeros (~300) y el refinado es
 * client-side. Así el asesor no necesita conocer los slugs, y la familia
 * (Interrail / Eurail) la resuelve el proveedor por la residencia.
 */
export const useSearchPasses = (payload: PassesSearchPayload | null) =>
  useVerticalSearch('passes', payload);

/** Cross-sell: pases que cubren el viaje de una oferta de tren ya cotizada. */
export const usePassesForOffer = () =>
  useMutation({
    mutationFn: async (payload: {
      provenanceToken: string;
      passengers?: PassPassengerPayload[];
      currency?: string;
    }): Promise<SearchEnvelope> => {
      const response = await client.post<SearchEnvelope>(
        'rutero/passes/for-offer',
        payload,
      );
      return response.data;
    },
    retry: false,
  });

export const useSearchCircuits = (payload: CircuitsSearchPayload | null) =>
  useVerticalSearch('circuits', payload);

/**
 * Capacidades de los conectores de la agencia: se usan para OCULTAR acciones
 * que el proveedor no soporta (tickets, política, extras) en vez de que el
 * agente las descubra con un error al click.
 */
export const useConnectorCapabilities = (enabled = true) =>
  useQuery({
    ...connectorCapabilitiesQueryOptions(CONFIG.AGENCY_ID),
    enabled,
    queryFn: async (): Promise<ConnectorInfo[]> => {
      const response = await client.get<ConnectorInfo[]>('rutero/connectors');
      return response.data;
    },
  });

export interface PlacesAutocompleteOptions {
  kinds?: PlaceKind[];
  /**
   * Ids de CONECTOR (`deriveServedByForVertical`), no de vertical ni de
   * `provider`. `PlaceAutocomplete` los deriva; este hook solo los reenvía.
   */
  servedBy?: string[];
  countries?: string[];
  group?: boolean;
  limit?: number;
  /**
   * El caller decide explícitamente cuándo consultar (además de `q`): sirve
   * para no disparar mientras la autoridad de capabilities no está lista o
   * `servedBy` quedó vacío — un `servedBy` vacío significa "sin filtro" para
   * el backend, no "sin conectores", así que dispararía sin la restricción
   * que el picker necesita.
   */
  enabled?: boolean;
}

/**
 * Autocomplete de destinos (`plc_*`) contra el maestro de lugares. Uso
 * directo del hook de bajo nivel; `PlaceAutocomplete` es quien arma
 * `servedBy` a partir del vertical y decide `enabled`.
 */
export const usePlacesAutocomplete = (
  q: string,
  opts: PlacesAutocompleteOptions,
) => {
  const normalizedQ = q
    .trim()
    .slice(0, PLACES_AUTOCOMPLETE_LIMITS.queryMaxLength);
  const lang = PLACES_LANGS.includes(i18next.language as PlacesLang)
    ? (i18next.language as PlacesLang)
    : undefined;
  // `servedBy` presente-pero-vacío ES la restricción (ningún conector
  // configurado para el vertical): distinto de `undefined` ("sin filtro").
  // El backend interpreta `[]` como "sin filtro" igual que ausente, así
  // que acá se corta ANTES de la request — nunca se le manda `[]`.
  const servedByBlocksQuery =
    opts.servedBy !== undefined && opts.servedBy.length === 0;
  // El default va acá y no en cada picker: los cuatro omitían `limit` y
  // heredaban el 10 del contrato sin haberlo elegido. Un caller que necesite
  // otro número lo sigue pasando.
  const limit = opts.limit ?? PLACES_PICKER_LIMIT;
  const query = useQuery({
    ...ownershipCatalogQueryOptions(
      CONFIG.AGENCY_ID,
      'places',
      'autocomplete',
      opts.kinds,
      opts.servedBy,
      opts.countries,
      opts.group,
      limit,
      lang,
      normalizedQ,
    ),
    queryFn: async (): Promise<PlacesAutocompleteResponse> => {
      const response = await client.get<PlacesAutocompleteResponse>(
        'places/autocomplete',
        {
          params: {
            q: normalizedQ,
            kinds: opts.kinds?.length ? opts.kinds.join(',') : undefined,
            servedBy: opts.servedBy?.length
              ? opts.servedBy.join(',')
              : undefined,
            countries: opts.countries?.length
              ? opts.countries.join(',')
              : undefined,
            group: opts.group ? 'true' : undefined,
            limit,
            lang,
          },
        },
      );
      return response.data;
    },
    enabled:
      (opts.enabled ?? true) &&
      !servedByBlocksQuery &&
      normalizedQ.length >= PLACES_AUTOCOMPLETE_LIMITS.queryMinLength,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  return withOwnershipCatalogAuthority(query);
};

/**
 * Coordenadas de un lugar canónico ya elegido, para el mapa del recorrido
 * (#2247). Es la fuente que cubre las búsquedas RECONSTRUIDAS: la URL y las
 * recientes solo guardan el `plc_*`, así que sin esto el mapa dependía de que
 * la selección siguiera viva en el formulario.
 *
 * Deliberadamente FUERA de `withOwnershipCatalogAuthority`, a diferencia del
 * resto de la familia: ese envoltorio esconde el dato mientras revalida
 * porque unas filas viejas no pueden alimentar una SELECCIÓN nueva — acá no
 * se selecciona nada, es display puro, y esconderlo haría parpadear el mapa
 * en cada vuelta a la pestaña.
 *
 * `retry: false` y sin toast: si el maestro no puede resolverlo (no existe,
 * está caído, la agencia no tiene el feature) simplemente no hay mapa, que es
 * el mismo comportamiento que un lugar sin geo publicada.
 */
export const usePlaceCoords = (placeId: string | undefined) =>
  useQuery({
    queryKey: [QueryKeys.getPlaceCoords, CONFIG.AGENCY_ID, placeId],
    queryFn: async (): Promise<PlaceCoordsLookup> => {
      const response = await client.get<PlaceCoordsLookup>(
        `places/${placeId}/coords`,
      );
      return response.data;
    },
    // La forma se valida acá y en el borde HTTP con la MISMA definición: un
    // valor que no es `plc_*` no se manda a preguntar.
    enabled: Boolean(placeId) && PLACE_ID_PATTERN.test(placeId ?? ''),
    // El árbol de lugares cambia una vez por mes; el backend ya cachea 24 h.
    staleTime: 60 * 60 * 1000,
    retry: false,
  });

export interface GeocodeAutocompleteOptions {
  /**
   * `null` = todavía no hay sesión de tipeo activa (el input no tiene foco):
   * la query queda deshabilitada. Lo genera `TransferPointAutocomplete` al
   * enfocar, no este hook — acá solo se reenvía.
   */
  sessionToken: string | null;
  /** País del VIAJE (ISO-3166 alpha-2), si se conoce. Mejora relevancia. */
  country?: string;
  enabled?: boolean;
}

/**
 * Paso 1 del geocode de direcciones/POIs (Google vía ubikeishon,
 * docs/PLACES.md §9). Mecanismo DISTINTO del autocomplete de destinos de
 * arriba: sin cache (semántica de sesión de Google), `staleTime: 0` — cada
 * tecla es una consulta nueva, nunca se sirve una predicción vieja de otra
 * sesión. Uso directo del hook de bajo nivel; `TransferPointAutocomplete`
 * (`features/travel-services/components/`) decide cuándo hay `sessionToken` y
 * arma `country`.
 *
 * El mínimo de `GEOCODE_QUERY_MIN_LENGTH` se impone ACÁ (mismo patrón que
 * `usePlacesAutocomplete` con `PLACES_AUTOCOMPLETE_LIMITS.queryMinLength`),
 * no confiado al caller: sin esto, una pausa de tipeo con 1-2 caracteres
 * dispara igual la request (el backend la acepta, degrada con
 * `provider.reason: 'too_short'` sin llamar a Google — pero es un roundtrip
 * evitable a client-api/ubikeishon en cada pausa corta).
 */
export const useGeocodeAutocomplete = (
  q: string,
  opts: GeocodeAutocompleteOptions,
) => {
  const normalizedQ = q.trim().slice(0, PLACES_GEOCODE_LIMITS.queryMaxLength);
  return useQuery({
    queryKey: [
      QueryKeys.getPlacesGeocodeAutocomplete,
      CONFIG.AGENCY_ID,
      normalizedQ,
      opts.sessionToken,
      opts.country,
    ],
    queryFn: async (): Promise<GeocodeAutocompleteResponse> => {
      const response = await client.get<GeocodeAutocompleteResponse>(
        'places/geocode/autocomplete',
        {
          params: {
            q: normalizedQ,
            sessionToken: opts.sessionToken,
            country: opts.country,
          },
        },
      );
      return response.data;
    },
    enabled:
      (opts.enabled ?? true) &&
      Boolean(opts.sessionToken) &&
      normalizedQ.length >= GEOCODE_QUERY_MIN_LENGTH,
    staleTime: 0,
    // `staleTime: 0` es deliberado (la sesión de Google tiene su propia
    // semántica, no se reusa una predicción vieja para una query nueva) — pero
    // sin esto, el default de React Query (`refetchOnWindowFocus: true`, no hay
    // override global) convierte cada vuelta a la pestaña en OTRA llamada
    // paga, gastando el cupo diario de la agencia sin que nadie escriba nada.
    // La query se rehace igual cuando cambia `q` (cambia la key), que es la
    // única frescura que importa acá.
    refetchOnWindowFocus: false,
    retry: false,
  });
};

/**
 * Paso 2: coordenadas del `placeId` elegido. A diferencia del paso 1, SÍ
 * puede fallar con una excepción real (cupo agotado, throttle, no
 * configurado) — ver docs/PLACES.md §9.
 */
export const useGeocodeResolve = () =>
  useMutation({
    mutationFn: async (payload: {
      placeId: string;
      sessionToken: string;
    }): Promise<GeocodePoint> => {
      const response = await client.post<GeocodePoint>(
        'places/geocode/resolve',
        payload,
      );
      return response.data;
    },
    retry: false,
  });

export const useSearchCars = (payload: CarsSearchPayload | null) =>
  useVerticalSearch('cars', payload);

/** Cada click crea una generación independiente aun si React Query ya cacheó el payload. */
export const useSearchCarRatePlans = (
  payload: CarsSearchPayload | null,
  generation: readonly [number, string],
) => useVerticalSearch('cars', payload, ['rate-plan-comparison', ...generation]);

/**
 * Trenes: el proveedor cotiza EN VIVO (~45s). El axios del backend ya usa 60s
 * para este vertical; acá sin retry para no duplicar una espera tan larga.
 */
export const useSearchRail = (payload: RailSearchPayload | null) =>
  useVerticalSearch('rail', payload);

export const useSearchEsims = (payload: EsimsSearchPayload | null) =>
  useVerticalSearch('esims', payload);

export const useSearchTours = (payload: ToursSearchPayload | null) =>
  useVerticalSearch('tours', payload);

/**
 * Los searches son QUERIES, no mutations: así el resultado queda cacheado por
 * sus parámetros y volver de otro vertical no re-dispara la búsqueda.
 *
 * `staleTime` está deliberadamente por DEBAJO del TTL local (15 min): pasada
 * la ventana React Query marca la generación stale y puede refrescarla, pero no
 * la oculta. La selección usa aparte su `dataUpdatedAt` y deja de ser posible
 * al alcanzar el TTL, aunque el dato siga visible como referencia.
 */
const SEARCH_STALE_TIME_MS = SEARCH_FRESH_MS;
const SEARCH_CACHE_TIME_MS = SEARCH_CACHE_MS;

/**
 * La agencia entra en TODA key de search: cambiar de agencia jamás puede
 * servir el cache de otra (21.2).
 */
export const ruteroSearchKey = (vertical: string, payload: unknown) => {
  const authorityKey = quoteSearchAuthorityKey(
    vertical,
    CONFIG.AGENCY_ID,
    payload,
  );
  return [
    QueryKeys.getRuteroSearch,
    ...authorityKey,
  ];
};

function useVerticalSearch<TPayload>(
  vertical: string,
  payload: TPayload | null,
  keySuffix: readonly unknown[] = [],
) {
  return useQuery({
    queryKey: [...ruteroSearchKey(vertical, payload), ...keySuffix],
    queryFn: async (): Promise<SearchEnvelope> => {
      const response = await client.post<SearchEnvelope>(
        `rutero/${vertical}/search`,
        payload,
      );
      return response.data;
    },
    // Sin parámetros completos no hay búsqueda que hacer. Un cambio de key
    // tampoco puede proyectar la respuesta anterior como autoridad de la nueva.
    ...verticalSearchAuthorityOptions(payload !== null),
    staleTime: SEARCH_STALE_TIME_MS,
    cacheTime: SEARCH_CACHE_TIME_MS,
    retry: false,
  });
}

export const useSearchTransfers = (payload: TransfersSearchPayload | null) =>
  useVerticalSearch('transfers', payload);

// ── Bookings (fase 5) ──────────────────────────────────────────────────────

export interface RuteroPassengerPayload {
  /** Perfil canónico que originó esta posición; nunca se envía al proveedor. */
  travelerId?: string;
  /** Provenance sólo del draft UI; permite limpiar PII al cambiar de perfil. */
  travelerSourceId?: string;
  /** Selección documental del draft de Transfers; nunca viaja al API. */
  identityDocument?: { type: 'passport' | 'dni' | 'ci'; id?: string; unresolved?: true };
  type: RuteroPassengerType;
  firstName: string;
  lastName: string;
  birthDate?: string;
  email?: string;
  phone?: string;
  nationality?: string;
  passportNumber?: string;
  passportExpiry?: string;
  /** Vuelo de llegada (ej. IB6012); el del contacto va al tramo de ida. */
  flightNumber?: string;
  /** Vuelo de regreso (RT): va al tramo de vuelta. */
  returnFlightNumber?: string;
  /**
   * Indicaciones para el CONDUCTOR (traslados). Write-only por contrato:
   * viaja al operador en el hold y no vuelve en ninguna lectura — vive solo
   * en el formulario. Tope del hub: 500, sin recorte (400 si se pasa).
   */
  driverNotes?: string;
  /** País de residencia (ISO): hay operadores que tarifan por residencia. */
  countryOfResidence?: string;
  roomIndex?: number;
  /** Tratamiento ("Sr.", "Sra."): algunos operadores lo piden para emitir. */
  title?: string;
  isContactPerson?: boolean;
}

export interface HoldBookingPayload {
  transferTransports?: TransferTransports;
  attemptId: string;
  caseId: number;
  vertical: string;
  provenanceToken: string;
  passengers: RuteroPassengerPayload[];
  providerHold?: boolean;
  pickupId?: string;
  ticketQuantities?: RuteroOfferOptionsSelection['ticketQuantities'];
  /** Ferry: una entrada POR PLAZA (repetir el id = varias plazas). */
  accommodationIds?: string[];
  ticketTypeId?: string;
  /** Tours: identidad opaca de la sesión cotizada. */
  sessionId?: string;
  occupancy?: RuteroOfferOptionsSelection['occupancy'];
  supplements?: RuteroOfferOptionsSelection['supplements'];
  quoteToken?: string;
  /**
   * Adicionales de la oferta a reservar en el mismo hold, por su
   * `detailBookingCode` — una entrada POR UNIDAD (#2383). SÍ entra en
   * `holdPayloadIdentity`: otra selección de adicionales es otra compra, o
   * sea otra operación con su propia idempotency key.
   */
  optionalItemCodes?: string[];
  /**
   * Origen y destino QUE SE DIBUJARON, para el mapa de la reserva (#2228,
   * #2229): los que resolvió el proveedor si la oferta los trae, y si no los
   * del pedido. Es dato de display: queda FUERA de `holdPayloadIdentity` (y
   * del fingerprint del backend) porque la idempotency key identifica la
   * OPERACIÓN — un punto distinto no puede rotarla y crear una segunda
   * reserva.
   */
  routePoints?: RouteMapPoint[];
}

/** Fila local de travel_service_bookings (bandido = sistema de registro). */
/** Una opción cerrada del operador: se muestra `label`, se responde `value`. */
export interface PendingQuestionOption {
  value: string;
  label: string;
}

/** Lo que el operador exige responder para poder confirmar. */
export interface PendingQuestion {
  code: string;
  prompt: string;
  mandatory?: boolean;
  /**
   * Forma que el HUB normalizó para la respuesta (vocabulario cerrado del
   * contrato): `text` libre, `number` un número, `date` en `YYYY-MM-DD`,
   * `boolean` un sí/no, `options` una elección cerrada, `other` una forma que
   * el hub no reconoce. Ausente = texto libre. Acá no se interpreta nada del
   * proveedor: se elige el control.
   */
  dataType?: RuteroQuestionDataType;
  /**
   * Índice 0-based del pasajero al que corresponde ESTA copia, cuando el
   * operador pide el mismo dato una vez por pasajero. La pregunta llega
   * repetida —mismo `code`, distinto índice— y cada copia se responde por
   * separado. Ausente = la pregunta es del booking entero.
   */
  passengerIndex?: number;
  /**
   * Las opciones cerradas, cuando el operador publicó la lista. Puede faltar
   * aun con `dataType: 'options'`: ahí la respuesta viaja textual.
   */
  options?: PendingQuestionOption[];
  /** Solo con `other`: la declaración cruda, como pista. No se interpreta. */
  dataTypeRaw?: string;
}

export interface QuestionAnswer {
  code: string;
  value: string;
  /** Se repite el de la pregunta cuando vino por pasajero. */
  passengerIndex?: number;
}

export interface TravelBooking extends CancellationPenaltyFields {
  source?: 'rutero' | 'hotelrs';
  hotelSnapshot?: import('@bandido/shared/types').HotelReservationSnapshot;
  id: string;
  agencyId: number;
  /** Contexto comercial. Falta únicamente en reservas históricas. */
  caseId?: number | null;
  case?: {
    id: number;
    name: string;
    client: {
      id: string;
      firstName: string;
      lastName: string;
    };
  };
  vertical: string;
  provider?: string;
  ruteroBookingId?: string;
  providerReference?: string;
  activeOperation?: 'confirm' | 'cancel';
  activeOperationStatus?: 'in_progress' | 'ambiguous' | 'reconciling' | 'stalled';
  activeOperationRecheckAt?: string;
  confirmBlock?: RuteroConfirmBlock;
  state: 'draft' | 'quoted' | 'held' | 'confirmed' | 'cancelled' | 'failed';
  holdAttemptState?:
    | 'ready'
    | 'in_flight'
    | 'transport_ambiguous'
    | 'provider_unknown_state'
    | 'adoption_pending'
    | 'completed'
    | 'clean_failed';
  recoveryMode: RuteroBookingRecoveryMode;
  providerStatus?: string;
  unknownProviderState: boolean;
  /**
   * Lo que el operador exige responder para confirmar. Viene con el hold y
   * con toda lectura posterior de la reserva (la fila las guarda), así que
   * cerrar la pantalla y volver no las pierde. Ausente = nada que preguntar.
   */
  pendingQuestions?: PendingQuestion[];
  pricingModel: 'net' | 'commissionable';
  netAmountMinor?: number;
  commissionAmountMinor?: number;
  commissionCurrency?: string;
  pricingBaseAmountMinor?: number;
  pricingBaseCurrency?: string;
  markupRuleId?: string;
  markupType?: 'percentage' | 'fixed';
  markupValue?: number;
  finalAmountMinor?: number;
  currency?: string;
  /**
   * El precio del PROVEEDOR congelado al reservar (`quotedPrice` del
   * contrato), en `currency`. Es la foto que no se mueve: `price` vigente del
   * hub puede cambiar en confirm/cancel y por eso NO es este campo. Ausente en
   * reservas sin snapshot comercial congelado (y en las anteriores al campo).
   */
  quotedAmountMinor?: number;
  /**
   * Los renglones con los que el proveedor EXPLICA ese importe (impuestos,
   * tasas). Ausente = el proveedor no publica desglose: se muestra el precio
   * sin él. Nunca se suman para reemplazar el total — el autoritativo es
   * `quotedAmountMinor`.
   */
  quotedTaxes?: RuteroBookingTax[];
  fareSnapshot?: { name?: string; title?: string } & Record<string, unknown>;
  passengers?: RuteroPassengerPayload[];
  /**
   * Origen y destino tal como se dibujaron y se guardaron al holdear, con el
   * rótulo de si los resolvió el proveedor o son los del pedido. Ausentes en
   * reservas anteriores a este campo y en las que se pidieron sin coordenadas
   * (un IATA a mano, un lugar del maestro): ahí no hay mapa que dibujar.
   */
  routePoints?: RouteMapPoint[];
  /**
   * Opciones exactas enviadas al hold, proyectadas desde la reserva durable.
   * Es lo que el AGENTE pidió, no lo que el proveedor confirmó — para eso
   * está `reservedOptions`, que manda cuando está presente.
   */
  selectedOptions?: RuteroPublicSelectedOptions;
  /**
   * Lo que el proveedor CONFIRMÓ que quedó reservado al holdear (con nombre y
   * cantidad). Fuente preferida sobre `selectedOptions` para mostrarle al
   * agente qué se reservó; ausente en bookings de antes de este campo o en
   * verticales sin opciones por oferta.
   */
  reservedOptions?: RuteroBookingSelectedOptions;
  /**
   * Adicionales que el operador confirmó para el hold. Se mantiene separado
   * de `reservedOptions` (acomodación/tarifa/sesión) igual que en la fila.
   */
  confirmedOptionalItems?: RuteroConfirmedOptionalItem[] | null;
  /**
   * CUÁNDO se presta el servicio (`YYYY-MM-DD`) y a qué hora LOCAL del punto
   * (`HH:mm`), tal como los publica el hub. Se refrescan con cada lectura
   * autoritativa de la reserva, así que pueden MEJORAR (la hora real de
   * recogida de un traslado en vez de la que se pidió).
   *
   * `serviceDate` ausente = el hub no publica momento para esta reserva
   * (`esims`: la vigencia arranca al escanear el QR; o un conector del que
   * todavía no puede derivarlo): se muestra "—", nunca una fecha inventada.
   * Nunca hay `serviceTime` sin `serviceDate`, y la hora es de PARED del lugar
   * — no se convierte a la zona del navegador.
   */
  serviceDate?: string;
  serviceTime?: string;
  /** Momento operativo completo, refrescado desde `booking.service.start`. */
  serviceStartMoment?: import('@bandido/shared/types').RuteroServiceMoment | null;
  /** Devolución/fin operativo, refrescado desde `booking.service.end`. */
  serviceEndMoment?: import('@bandido/shared/types').RuteroServiceMoment | null;
  serviceEndDate?: string;
  serviceEndTime?: string;
  /**
   * Los TRAMOS del traslado (`booking.legs` del hub, solo transfers): la
   * recogida con su estado, el punto de encuentro y la categoría del
   * vehículo, por tramo (roundtrip = 2). Se refrescan con cada lectura
   * autoritativa — el punto de encuentro aparece típicamente recién cuando
   * hay operador asignado y llega con el sync. Ausente/`null` = el hub no
   * publica tramos (otros verticales, proveedor sin detalle): la sección no
   * se dibuja.
   */
  legs?: RuteroBookingLeg[] | null;
  /**
   * Cómo se paga el servicio (`ppd` prepago / `pod` pago en destino), tal como
   * lo publica el hub. Es lo que DECIDE qué documento del cliente se arma —un
   * voucher o una confirmación—, nunca una heurística sobre los importes.
   * `null`/ausente = el proveedor no distingue formas de pago.
   */
  paymentType?: RuteroPaymentType | null;
  /**
   * Quién comercializa la reserva (razón social + identificación fiscal): con
   * esto se arma la leyenda "Bookable and payable by {name} (VAT {vatNumber})"
   * que exigen las certificaciones. NO es quien opera el servicio.
   */
  supplier?: RuteroBookingSupplier | null;
  /**
   * Los datos del voucher que emitió el proveedor, para los que no entregan un
   * documento propio. ⚠️ SOBREVIVE a la cancelación —es la foto de lo que el
   * proveedor emitió—: que venga con valor NO habilita imprimir, hay que mirar
   * `state` primero.
   */
  voucher?: RuteroBookingVoucher | null;
  /** Titular (pasajero de contacto) y tamaño del grupo, congelados al reservar. */
  holderName?: string;
  paxCount?: number;
  expiresAt?: string;
  holdRetention?: 'none' | 'provider' | 'provider-expiry-unpublished' | null;
  ticketsReady: boolean;
  tickets?: TravelTicket[];
  ticketsExpiresAt?: string | null;
  /**
   * Reembolso informado por el proveedor AL CANCELAR (no se persiste: solo
   * viaja en la respuesta del cancel). `pending` = el importe real se confirma
   * en la liquidación del proveedor, no acá.
   */
  refund?: { amount: RuteroMoney; state: 'pending' | 'succeeded' | 'error' };
  /**
   * El item de venta al que quedó enganchada la reserva. Ausente = plata sin
   * contabilizar todavía, que es un estado VISIBLE del listado y no una celda
   * vacía.
   */
  saleOrderItemId?: string;
  createdBy: string;
  /** Email del usuario que reservó (resuelto por el backend). */
  createdByEmail?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Documento del viaje. `barcode` es una imagen para escanear en el embarque y
 * `code` un alfanumérico que se activa en la app del proveedor (no hay nada
 * que descargar).
 */
export interface TravelTicket {
  kind: 'pdf' | 'checkin' | 'collection' | 'file' | 'selfservice' | 'barcode' | 'code';
  url?: string;
  reference?: string;
  language?: string;
  /** Asociación contractual a un tramo; nunca se deduce por posición. */
  legId?: string;
  direction?: 'outbound' | 'return';
  description?: string;
  esimInstallation?: {
    iccid: string;
    smdpAddress?: string;
    activationCode?: string;
    qrCodeData?: string;
  };
}

export interface TicketsResult {
  ready: boolean;
  tickets: TravelTicket[];
  /**
   * Vencimiento de las URLs: varios operadores las firman por horas. Pasado
   * este momento hay que volver a pedirlas, no mostrar las guardadas.
   */
  expiresAt?: string;
}

export type CancellationPolicy = import('@bandido/shared/types').RuteroCancellationPolicy;

export interface TravelBookingsPage {
  data: TravelBooking[];
  totalRecords: number;
  totalPages: number;
  /**
   * Lo que la respuesta dice de sí misma: la pestaña servida, los contadores
   * de las cuatro (calculados con el MISMO ámbito y los mismos filtros) y si
   * la cola sin paginar vino recortada. Ausente en respuestas de una versión
   * anterior del endpoint: la pantalla no puede asumirlo.
   */
  meta?: RuteroBookingListMeta;
}

/**
 * La cola de trabajo. `params` sale de UNA sola derivación
 * (`bookingListQueryParams`) y entra completo en la query key: el ámbito y la
 * pestaña son parte de la identidad de la respuesta, así que servir la cache
 * de otro ámbito mostraría las reservas de otro asesor bajo el selector
 * equivocado (§21.2).
 *
 * `enabled` es del CALLER: mientras el ámbito no se resuelve —"Mías" sin
 * usuario cargado— no hay pedido que valga, y pedir sin `createdBy` traería
 * las de toda la agencia.
 */
export const useTravelBookings = (
  agencyId: number | 'all',
  params: Record<string, string | number | boolean>,
  options?: { enabled?: boolean; userId?: string },
) =>
  useQuery({
    queryKey: [QueryKeys.getTravelBookings, 'list', agencyId, options?.userId, params],
    queryFn: async (): Promise<TravelBookingsPage> => {
      const response = await client.get<TravelBookingsPage>('rutero/bookings', {
        params: agencyId === 'all' ? { ...params, allAgencies: true } : params,
        headers: agencyId === 'all' ? undefined : { 'Resource-Agency-Id': String(agencyId) },
      });
      return response.data;
    },
    enabled: Boolean(agencyId) && options?.enabled !== false,
    refetchOnWindowFocus: true,
    // La cola se relee entera al cambiar de pestaña o de ámbito: mostrar la
    // anterior mientras llega la nueva evita que la pantalla parpadee a vacío.
    keepPreviousData: true,
  });

export async function fetchTravelBookingById(
  bookingId: string,
): Promise<TravelBooking> {
  const response = await client.get<TravelBooking>(`rutero/bookings/${bookingId}`);
  return response.data;
}

export const useTravelBookingRefresh = () => {
  const queryClient = useQueryClient();
  return async (
    bookingId: string,
    onBooking: (booking: TravelBooking) => void,
  ): Promise<TravelBooking | null> => refreshBookingAfterOperationError({
    bookingId,
    fetchBooking: fetchTravelBookingById,
    onBooking,
    queryClient,
  });
};

const useBookingMutation = <TVariables>(
  mutationFn: (variables: TVariables) => Promise<TravelBooking>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (booking) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.getTravelBookings] });
      // El voucher tiene clave propia: se invalida el de ESTA reserva, no uno por fila.
      queryClient.invalidateQueries({ queryKey: voucherQueryKey(booking.id) });
    },
  });
};

export const useHoldBooking = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: HoldBookingPayload): Promise<TravelBooking> => {
      const response = await client.post<TravelBooking>(
        'rutero/bookings/hold',
        payload,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.getTravelBookings] });
      // El hold adjunta el item a la sale order activa: refrescar carrito y paxes.
      queryClient.invalidateQueries({ queryKey: [QueryKeys.saleOrder] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.getSaleOrdersDetails] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.getSaleOrdersPaxes] });
    },
  });
};

/** Reintenta el request exacto que el backend guardó para la key original. */
export const useRetryHoldBooking = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (bookingId: string): Promise<TravelBooking> => {
      const response = await client.post<TravelBooking>(
        `rutero/bookings/${bookingId}/retry-hold`,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.getTravelBookings] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.saleOrder] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.getSaleOrdersDetails] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.getSaleOrdersPaxes] });
    },
  });
};

export const travelBookingByItemQueryKey = (itemId?: string) => [
  QueryKeys.getTravelBookings,
  'by-item',
  itemId,
];

export const fetchTravelBookingByItem = async (
  itemId: string,
): Promise<TravelBooking> => {
  const response = await client.get<TravelBooking>(
    `rutero/bookings/by-item/${itemId}`,
  );
  return response.data;
};

/** Booking vinculado a un item de sale order (vista del item). */
export const useTravelBookingByItem = (itemId?: string) =>
  useQuery({
    queryKey: travelBookingByItemQueryKey(itemId),
    queryFn: async (): Promise<TravelBooking> => {
      if (!itemId) {
        throw new Error('Missing sale-order item id');
      }
      return fetchTravelBookingByItem(itemId);
    },
    enabled: Boolean(itemId),
    refetchOnWindowFocus: true,
    retry: false,
  });

/**
  * Confirmar puede exigir respuestas: algunos productos traen preguntas del
  * operador (`pendingQuestions`) que hay que contestar para emitir. Sin
  * preguntas se confirma sin body.
  */
export const useConfirmBooking = () =>
  useBookingMutation(
    async (input: string | { bookingId: string; answers?: QuestionAnswer[] }) => {
      const { bookingId, answers } =
        typeof input === 'string' ? { bookingId: input, answers: undefined } : input;
      const response = await client.post<TravelBooking>(
        `rutero/bookings/${bookingId}/confirm`,
        answers?.length ? { answers } : undefined,
      );
      return response.data;
    },
  );

export const useCancelBooking = () =>
  useBookingMutation(async (bookingId: string) => {
    const response = await client.post<TravelBooking>(
      `rutero/bookings/${bookingId}/cancel`,
    );
    return response.data;
  });

export const useSyncBooking = () =>
  useBookingMutation(async (bookingId: string) => {
    const response = await client.post<TravelBooking>(
      `rutero/bookings/${bookingId}/sync`,
    );
    return response.data;
  });

export const useResolveManualBookingId = () =>
  useBookingMutation(async ({
    bookingId,
    ruteroBookingId,
  }: {
    bookingId: string;
    ruteroBookingId: string;
  }) => {
    const response = await client.post<TravelBooking>(
      `rutero/bookings/${bookingId}/manual-reconciliation/verified-booking`,
      { ruteroBookingId },
    );
    return response.data;
  });

export const useResolveManualNoRemoteBooking = () =>
  useBookingMutation(async (bookingId: string) => {
    const response = await client.post<TravelBooking>(
      `rutero/bookings/${bookingId}/manual-reconciliation/no-remote-booking`,
    );
    return response.data;
  });

/** Extras de un booking (excursiones, paquetes opcionales, sillas de bebé). */
export const useBookingExtras = (bookingId?: string, enabled = true) =>
  useQuery({
    queryKey: [QueryKeys.getTravelBookings, 'extras', bookingId],
    queryFn: async (): Promise<ExtrasResult> => {
      const response = await client.get<ExtrasResult>(
        `rutero/bookings/${bookingId}/extras`,
      );
      return response.data;
    },
    enabled: Boolean(bookingId) && enabled,
    retry: false,
  });

const useExtraMutation = <TVariables>(
  bookingId: string | undefined,
  mutationFn: (variables: TVariables) => Promise<ExtrasResult>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (data) => {
      // La respuesta ES el estado completo de extras: se siembra el cache en
      // vez de refetchear (el proveedor puede tardar en responder).
      queryClient.setQueryData(
        [QueryKeys.getTravelBookings, 'extras', bookingId],
        data,
      );
    },
  });
};

export const useAddExtra = (bookingId?: string) =>
  useExtraMutation(
    bookingId,
    async (payload: { extraId: string; date?: string; passengers?: number[] }) => {
      const response = await client.post<ExtrasResult>(
        `rutero/bookings/${bookingId}/extras`,
        payload,
      );
      return response.data;
    },
  );

export const useRemoveExtra = (bookingId?: string) =>
  useExtraMutation(bookingId, async (purchaseId: string) => {
    const response = await client.delete<ExtrasResult>(
      `rutero/bookings/${bookingId}/extras/${encodeURIComponent(purchaseId)}`,
    );
    return response.data;
  });

export const useCancellationPolicy = () =>
  useMutation({
    mutationFn: async (bookingId: string): Promise<CancellationPolicy> => {
      const response = await client.get(
        `rutero/bookings/${bookingId}/cancellation-policy`,
      );
      return response.data;
    },
  });

export const useBookingTickets = (
  bookingId?: string,
  scopeKey: string | undefined = bookingId,
) => {
  const queryClient = useQueryClient();
  const [requestedBookingId, setRequestedBookingId] = useState<string | null>(null);
  const [requestedScopeKey, setRequestedScopeKey] = useState<string | null>(null);
  const [pendingResponses, setPendingResponses] = useState(0);
  const active =
    bookingTicketRequestIsActive(requestedBookingId, bookingId) &&
    bookingTicketScopeIsActive(requestedScopeKey, scopeKey);

  const query = useQuery({
    queryKey: bookingTicketsQueryKey(bookingId),
    queryFn: async (): Promise<TicketsResult> => {
      if (!bookingId) throw new Error('Booking id is required to fetch tickets');
      const response = await client.get(`rutero/bookings/${bookingId}/tickets`);
      return response.data;
    },
    enabled: active,
    keepPreviousData: false,
    retry: false,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    refetchInterval: (result?: TicketsResult) =>
      result?.ready === false
        ? bookingTicketPollDelay(pendingResponses)
        : false,
    onSuccess: (result) => {
      if (!result.ready) {
        setPendingResponses((current) => current + 1);
        return;
      }
      setPendingResponses(0);
      queryClient.invalidateQueries({ queryKey: [QueryKeys.getTravelBookings] });
      // Los tickets pueden haber vuelto: el voucher de ESTA reserva puede estar viejo.
      if (bookingId) {
        queryClient.invalidateQueries({ queryKey: voucherQueryKey(bookingId) });
      }
    },
    // Una caída no debe dejar un timer golpeando el mismo endpoint. El asesor
    // conserva la acción manual, que reinicia la ventana cuando corresponde.
    onError: () => {
      setPendingResponses(BOOKING_TICKET_POLL_DELAYS_MS.length + 1);
    },
  });

  const request = useCallback(() => {
    if (!bookingId) return;
    setPendingResponses(0);
    if (
      bookingTicketRequestIsActive(requestedBookingId, bookingId) &&
      bookingTicketScopeIsActive(requestedScopeKey, scopeKey)
    ) {
      void query.refetch();
      return;
    }
    setRequestedBookingId(bookingId);
    setRequestedScopeKey(scopeKey ?? null);
  }, [bookingId, query, requestedBookingId, requestedScopeKey, scopeKey]);

  const pollDelay = active && query.data?.ready === false
    ? bookingTicketPollDelay(pendingResponses)
    : false;

  return {
    data: active ? query.data : undefined,
    error: active ? query.error : null,
    isError: active && query.isError,
    isLoading: active && query.isFetching,
    isPolling: pollDelay !== false,
    pollingExhausted:
      active && query.data?.ready === false && pollDelay === false,
    request,
  };
};

/*
 * El voucher de agencia (docs/VOUCHERS.md §6). A diferencia del resto de
 * `rutero/bookings/:id/*`, estos endpoints reciben `agencyId` por query o por
 * cuerpo y no por el header `Agency-Id`: la descarga tiene que ser una URL
 * autocontenida. El valor sale de la FILA (`booking.agencyId`), no de la
 * configuración del cliente.
 */

/** El idioma del documento: el del asesor si el motor lo tiene; si no, español. */
export function voucherLangOf(language: string | undefined): VoucherTextLanguage {
  const short = (language ?? '').slice(0, 2).toLowerCase();
  return (VOUCHER_TEXT_LANGUAGES as readonly string[]).includes(short)
    ? (short as VoucherTextLanguage)
    : VOUCHER_TEXT_BASE_LANGUAGE;
}

/** El estado del documento de una reserva. */
export const useVoucherState = (
  booking: { id: string; agencyId: number },
  options: {
    enabled: boolean;
    lang: VoucherTextLanguage;
    /**
     * La fila del listado: N instancias por página. No se refresca al volver
     * a la pestaña —eso disparaba N GETs a la vez— y vive más en caché; la
     * invalidación por reserva (sync, tickets, socket) la sigue actualizando.
     */
    background?: boolean;
  },
) =>
  useQuery({
    queryKey: voucherQueryKey(booking.id),
    queryFn: async (): Promise<VoucherDocumentState> => {
      const response = await client.get<VoucherDocumentState>(
        `rutero/bookings/${booking.id}/voucher`,
        { params: { agencyId: booking.agencyId, lang: options.lang } },
      );
      return response.data;
    },
    enabled: options.enabled,
    retry: false,
    // Los links firmados y la frescura cambian con cada sync: no vale la pena
    // servir un estado de hace una hora, pero tampoco pedirlo en cada render.
    staleTime: options.background ? 120_000 : 30_000,
    refetchOnWindowFocus: !options.background,
  });

/**
 * Pide la generación. Devuelve el estado que la pantalla muestra mientras
 * tanto —nunca espera al PDF— y siembra la caché con él.
 */
export const useRequestVoucher = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      bookingId: string;
      agencyId: number;
      lang: VoucherTextLanguage;
    }): Promise<VoucherDocumentState> => {
      const response = await client.post<VoucherDocumentState>(
        `rutero/bookings/${input.bookingId}/voucher`,
        { agencyId: input.agencyId, lang: input.lang },
      );
      return response.data;
    },
    onSuccess: (state, input) => {
      queryClient.setQueryData(voucherQueryKey(input.bookingId), state);
    },
  });
};

/**
 * El PDF, como blob. No un link crudo: el API vive en otro origen y con un
 * `<a href>` el 404 de "ya no está" llegaba como una pestaña en blanco, sin
 * mensaje. Así el error pasa por `apiErrorStatus` como cualquier otro.
 */
export async function fetchVoucherFile(input: {
  bookingId: string;
  agencyId: number;
}): Promise<{ blob: Blob; contentDisposition?: string }> {
  const response = await client.get<Blob>(`rutero/bookings/${input.bookingId}/voucher/file`, {
    params: { agencyId: input.agencyId },
    responseType: 'blob',
  });
  const header = response.headers?.['content-disposition'];
  return {
    blob: response.data,
    ...(typeof header === 'string' ? { contentDisposition: header } : {}),
  };
}

/**
 * Manda el documento (mail al contacto del hold) o pide el link. La respuesta
 * es el RESULTADO (`resolved | partial | no_email | failed | uncertain`), nunca
 * un 200 vacío: el fallo del proveedor de mail viene adentro y se muestra.
 * Viaja con `attemptId`: la identidad del intento, que hace que un reintento
 * (timeout del cliente, doble clic) no mande un segundo mail. Invalida el
 * estado de la reserva porque el último envío se publica ahí —también cuando
 * el pedido no tuvo respuesta, porque la fila del intento ya existe.
 */
export const useSendVoucher = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      bookingId: string;
      agencyId: number;
      channel: VoucherSendChannel;
      attemptId: string;
    }): Promise<VoucherSendResult> => {
      const response = await client.post<VoucherSendResult>(
        `rutero/bookings/${input.bookingId}/voucher/send`,
        { agencyId: input.agencyId, channel: input.channel, attemptId: input.attemptId },
      );
      return response.data;
    },
    onSettled: (_result, _error, input) => {
      queryClient.invalidateQueries({ queryKey: voucherQueryKey(input.bookingId) });
    },
  });
};
